import React, { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { montarArvoreCamadas, achatarArvoreCamadas } from './canvasUtils';
import { DOCUMENTO_DESIGN as D } from '../../EditorDocumento.design';

const ICONE_POR_TIPO = {
  texto: 'mdi:format-text',
  imagem: 'mdi:image-outline',
  retangulo: 'mdi:rectangle-outline',
  linha: 'mdi:minus',
  itens: 'mdi:table-large',
  variavel: 'mdi:code-braces',
  data: 'mdi:calendar-outline',
  validade: 'mdi:calendar-clock-outline',
  dados_cliente: 'mdi:account-outline',
};

const LABEL_POR_TIPO = {
  texto: 'Texto',
  imagem: 'Imagem',
  retangulo: 'Retângulo',
  linha: 'Linha',
  itens: 'Tabela de itens',
  variavel: 'Variável',
  data: 'Data',
  validade: 'Validade',
  dados_cliente: 'Dados do cliente',
};

// Rótulo de uma camada: tipo + trecho do conteúdo quando for texto (ajuda a achar
// o elemento certo quando há vários textos na página).
function labelCamada(el) {
  if (el.nome) return el.nome;
  if (el.tipo === 'texto') {
    const trecho = String(el.conteudo?.html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (trecho) return trecho.length > 24 ? `${trecho.slice(0, 24)}…` : trecho;
  }
  if (el.tipo === 'variavel') return `{{${el.conteudo?.chave || 'variavel'}}}`;
  return LABEL_POR_TIPO[el.tipo] || el.tipo;
}

// Painel de camadas do editor canvas (sidebar direita, estilo Canva): lista os
// elementos da página ativa da frente para o fundo. Clicar seleciona no canvas
// (Ctrl/Cmd+clique adiciona à seleção, Shift+clique seleciona o intervalo),
// arrastar reordena a pilha, as setas movem UM passo, o cadeado tranca a camada
// e a lixeira remove. Elementos com o mesmo `grupo_id` aparecem aninhados sob uma
// linha de grupo, que move/tranca/remove todos os membros de uma vez.
export default function LayersSidebar({
  paginaAtiva,
  elementos,
  grupos,
  selectedIds = [],
  onSelectElemento,
  onSelectGrupo,
  onMoverCamada,
  onReordenar,
  onRemoverElemento,
  onToggleBloqueio,
  onAgrupar,
  onDesagrupar,
  onRenomearGrupo,
}) {
  const arvore = montarArvoreCamadas(elementos, grupos);
  // Lista achatada frente → fundo, usada como base de qualquer reordenação
  const ordemAtual = achatarArvoreCamadas(arvore);

  const [dragKey, setDragKey] = useState(null); // 'el:<id>' | 'grupo:<gid>'
  const [over, setOver] = useState(null); // { key, pos: 'above' | 'below' }
  const [recolhidos, setRecolhidos] = useState({}); // { [grupo_id]: true }
  const [renomeando, setRenomeando] = useState(null); // { grupo_id, valor }

  const selecionados = new Set(selectedIds);
  const porId = new Map((elementos || []).map(el => [el.el_id, el]));
  const idsSelecionados = selectedIds.filter(id => porId.has(id));
  const gruposSelecionados = [...new Set(idsSelecionados.map(id => porId.get(id)?.grupo_id).filter(Boolean))];
  const podeAgrupar = idsSelecionados.length > 1;
  const podeDesagrupar = gruposSelecionados.length > 0;

  const keyDoNo = (no) => no.tipo === 'grupo' ? `grupo:${no.grupo_id}` : `el:${no.el.el_id}`;

  const handleDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key === dragKey) { setOver(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientY - rect.top) < rect.height / 2 ? 'above' : 'below';
    setOver({ key, pos });
  };

  // Reordena no nível da ÁRVORE: um grupo inteiro se move como bloco; um membro só
  // se move entre os irmãos do próprio grupo (para sair, use "Desagrupar").
  const handleDrop = (e) => {
    e.preventDefault();
    const origem = dragKey;
    const alvo = over;
    setDragKey(null);
    setOver(null);
    if (!origem || !alvo || origem === alvo.key) return;

    const grupoDe = (key) => key.startsWith('el:') ? porId.get(key.slice(3))?.grupo_id || null : null;
    const origemGrupo = grupoDe(origem);
    const alvoGrupo = grupoDe(alvo.key);

    // Membro de grupo: só reordena dentro do próprio grupo
    if (origemGrupo || alvoGrupo) {
      if (origemGrupo !== alvoGrupo) return;
      const no = arvore.find(n => n.tipo === 'grupo' && n.grupo_id === origemGrupo);
      if (!no) return;
      const ids = no.filhos.map(f => f.el_id).filter(id => id !== origem.slice(3));
      let idx = ids.indexOf(alvo.key.slice(3));
      if (idx < 0) return;
      if (alvo.pos === 'below') idx += 1;
      ids.splice(idx, 0, origem.slice(3));
      const novaArvore = arvore.map(n => n.tipo === 'grupo' && n.grupo_id === origemGrupo
        ? { ...n, filhos: ids.map(id => porId.get(id)).filter(Boolean) }
        : n);
      onReordenar?.(achatarArvoreCamadas(novaArvore));
      return;
    }

    // Nó de topo (elemento solto ou grupo inteiro)
    const nos = arvore.filter(n => keyDoNo(n) !== origem);
    const noMovido = arvore.find(n => keyDoNo(n) === origem);
    if (!noMovido) return;
    let idx = nos.findIndex(n => keyDoNo(n) === alvo.key);
    if (idx < 0) return;
    if (alvo.pos === 'below') idx += 1;
    nos.splice(idx, 0, noMovido);
    onReordenar?.(achatarArvoreCamadas(nos));
  };

  const finalizarDrag = () => { setDragKey(null); setOver(null); };

  // Linha de um elemento (usada solta e dentro de grupo)
  const renderElemento = (el, { dentroDeGrupo = false } = {}) => {
    const key = `el:${el.el_id}`;
    const ativo = selecionados.has(el.el_id);
    const arrastando = key === dragKey;
    const marcadorAcima = over?.key === key && over.pos === 'above';
    const marcadorAbaixo = over?.key === key && over.pos === 'below';
    return (
      <div
        key={el.el_id}
        draggable={!el.bloqueado}
        onDragStart={(e) => { setDragKey(key); e.dataTransfer.effectAllowed = 'move'; }}
        onDragOver={(e) => handleDragOver(e, key)}
        onDrop={handleDrop}
        onDragEnd={finalizarDrag}
        className={`relative flex items-center gap-1 rounded-lg px-1 py-1.5 group transition-colors ${
          dentroDeGrupo ? 'ml-3' : ''
        } ${arrastando ? 'opacity-40' : ''} ${
          ativo ? 'bg-violet-50 dark:bg-violet-500/10' : 'hover:bg-gray-50 dark:hover:bg-[#383838]/50'
        }`}
        onClick={(e) => onSelectElemento?.(el.el_id, { toggle: e.ctrlKey || e.metaKey, intervalo: e.shiftKey })}
      >
        {marcadorAcima && <span className="absolute -top-0.5 left-1 right-1 h-0.5 rounded-full bg-[#7C3AED]" />}
        {marcadorAbaixo && <span className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full bg-[#7C3AED]" />}

        <Iconify
          icon={el.bloqueado ? 'mdi:lock-outline' : 'mdi:drag-vertical'}
          width={13}
          className={`flex-shrink-0 text-gray-300 dark:text-gray-600 ${el.bloqueado ? '' : 'cursor-grab active:cursor-grabbing'}`}
        />
        <Iconify
          icon={ICONE_POR_TIPO[el.tipo] || 'mdi:shape-outline'}
          width={13}
          className={`flex-shrink-0 ${ativo ? 'text-[#7C3AED]' : 'text-gray-400'}`}
        />
        <span className={`flex-1 min-w-0 truncate text-[11px] ${
          el.bloqueado ? 'text-gray-400 dark:text-gray-500 italic' : ativo ? 'text-[#7C3AED] font-medium' : 'text-gray-600 dark:text-[#ABABAD]'
        }`}>
          {labelCamada(el)}
        </span>
        <div className={`flex items-center transition-opacity flex-shrink-0 ${el.bloqueado ? '' : 'opacity-0 group-hover:opacity-100'}`}>
          {!el.bloqueado && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMoverCamada?.(el.el_id, 1); }}
                title="Avançar uma camada"
                className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10"
              >
                <Iconify icon="mdi:arrow-up" width={12} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMoverCamada?.(el.el_id, -1); }}
                title="Recuar uma camada"
                className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10"
              >
                <Iconify icon="mdi:arrow-down" width={12} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleBloqueio?.([el.el_id]); }}
            title={el.bloqueado ? 'Destrancar camada' : 'Trancar camada (não poderá ser editada)'}
            className={`p-0.5 rounded hover:bg-violet-50 dark:hover:bg-violet-500/10 ${
              el.bloqueado ? 'text-[#7C3AED]' : 'text-gray-400 hover:text-[#7C3AED]'
            }`}
          >
            <Iconify icon={el.bloqueado ? 'mdi:lock' : 'mdi:lock-open-variant-outline'} width={12} />
          </button>
          {!el.bloqueado && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemoverElemento?.([el.el_id]); }}
              title="Remover elemento"
              className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              <Iconify icon="mdi:trash-can-outline" width={12} />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Linha de um grupo: seleciona/tranca/remove todos os membros de uma vez
  const renderGrupo = (no) => {
    const key = `grupo:${no.grupo_id}`;
    const ids = no.filhos.map(f => f.el_id);
    const ativo = ids.length > 0 && ids.every(id => selecionados.has(id));
    const travado = no.filhos.every(f => f.bloqueado);
    const recolhido = !!recolhidos[no.grupo_id];
    const arrastando = key === dragKey;
    const marcadorAcima = over?.key === key && over.pos === 'above';
    const marcadorAbaixo = over?.key === key && over.pos === 'below';
    const editandoNome = renomeando?.grupo_id === no.grupo_id;

    return (
      <div key={no.grupo_id}>
        <div
          draggable={!travado && !editandoNome}
          onDragStart={(e) => { setDragKey(key); e.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(e) => handleDragOver(e, key)}
          onDrop={handleDrop}
          onDragEnd={finalizarDrag}
          className={`relative flex items-center gap-1 rounded-lg px-1 py-1.5 group transition-colors ${
            arrastando ? 'opacity-40' : ''
          } ${ativo ? 'bg-violet-50 dark:bg-violet-500/10' : 'hover:bg-gray-50 dark:hover:bg-[#383838]/50'}`}
          onClick={(e) => onSelectGrupo?.(no.grupo_id, { toggle: e.ctrlKey || e.metaKey })}
        >
          {marcadorAcima && <span className="absolute -top-0.5 left-1 right-1 h-0.5 rounded-full bg-[#7C3AED]" />}
          {marcadorAbaixo && <span className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full bg-[#7C3AED]" />}

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setRecolhidos(r => ({ ...r, [no.grupo_id]: !recolhido })); }}
            title={recolhido ? 'Expandir grupo' : 'Recolher grupo'}
            className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] flex-shrink-0"
          >
            <Iconify icon={recolhido ? 'mdi:chevron-right' : 'mdi:chevron-down'} width={13} />
          </button>
          <Iconify
            icon={travado ? 'mdi:folder-lock-outline' : 'mdi:folder-outline'}
            width={13}
            className={`flex-shrink-0 ${ativo ? 'text-[#7C3AED]' : 'text-gray-400'}`}
          />
          {editandoNome ? (
            <input
              autoFocus
              value={renomeando.valor}
              onChange={(e) => setRenomeando({ grupo_id: no.grupo_id, valor: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => { onRenomearGrupo?.(no.grupo_id, renomeando.valor.trim() || 'Grupo'); setRenomeando(null); }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') { onRenomearGrupo?.(no.grupo_id, renomeando.valor.trim() || 'Grupo'); setRenomeando(null); }
                if (e.key === 'Escape') setRenomeando(null);
              }}
              className="flex-1 min-w-0 text-[11px] px-1 py-0.5 rounded border border-[#7C3AED] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] focus:outline-none"
            />
          ) : (
            <span
              onDoubleClick={(e) => { e.stopPropagation(); setRenomeando({ grupo_id: no.grupo_id, valor: no.nome }); }}
              title="Duplo clique para renomear"
              className={`flex-1 min-w-0 truncate text-[11px] font-medium ${
                ativo ? 'text-[#7C3AED]' : 'text-gray-600 dark:text-[#ABABAD]'
              }`}
            >
              {no.nome} <span className="text-gray-400 font-normal">({no.filhos.length})</span>
            </span>
          )}
          <div className={`flex items-center transition-opacity flex-shrink-0 ${travado ? '' : 'opacity-0 group-hover:opacity-100'}`}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDesagrupar?.([no.grupo_id]); }}
              title="Desagrupar"
              className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10"
            >
              <Iconify icon="mdi:ungroup" width={12} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleBloqueio?.(ids, !travado); }}
              title={travado ? 'Destrancar grupo' : 'Trancar grupo'}
              className={`p-0.5 rounded hover:bg-violet-50 dark:hover:bg-violet-500/10 ${
                travado ? 'text-[#7C3AED]' : 'text-gray-400 hover:text-[#7C3AED]'
              }`}
            >
              <Iconify icon={travado ? 'mdi:lock' : 'mdi:lock-open-variant-outline'} width={12} />
            </button>
            {!travado && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemoverElemento?.(ids); }}
                title="Remover grupo"
                className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
              >
                <Iconify icon="mdi:trash-can-outline" width={12} />
              </button>
            )}
          </div>
        </div>
        {!recolhido && (
          <div className="border-l border-gray-100 dark:border-[#383838] ml-2.5 pl-0.5 space-y-0.5">
            {no.filhos.map(el => renderElemento(el, { dentroDeGrupo: true }))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={D.layersAside}>
      <div className="p-3">
        <p className={`${D.paletteTitle} flex items-center gap-1.5`}>
          <Iconify icon="mdi:layers-outline" width={14} className="text-gray-400" />
          Camadas · Página {paginaAtiva + 1}
        </p>

        {/* Ações da seleção múltipla */}
        {(podeAgrupar || podeDesagrupar) && (
          <div className="flex items-center gap-1 mb-2">
            {podeAgrupar && (
              <button
                type="button"
                onClick={() => onAgrupar?.(idsSelecionados)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-[10px] font-medium transition-colors"
              >
                <Iconify icon="mdi:group" width={13} />
                Agrupar ({idsSelecionados.length})
              </button>
            )}
            {podeDesagrupar && (
              <button
                type="button"
                onClick={() => onDesagrupar?.(gruposSelecionados)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-[#383838] text-gray-600 dark:text-[#ABABAD] hover:bg-gray-50 dark:hover:bg-[#383838] text-[10px] font-medium transition-colors"
              >
                <Iconify icon="mdi:ungroup" width={13} />
                Desagrupar
              </button>
            )}
          </div>
        )}

        {ordemAtual.length === 0 ? (
          <p className="text-[10px] text-gray-400 px-1">Nenhum elemento nesta página ainda.</p>
        ) : (
          <div className="space-y-0.5" onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }}>
            {arvore.map(no => no.tipo === 'grupo' ? renderGrupo(no) : renderElemento(no.el))}
          </div>
        )}
        <p className={`${D.paletteHint} mt-3`}>
          A primeira camada fica na frente. Arraste para reordenar. Ctrl+clique seleciona várias para agrupar; o cadeado tranca a camada.
        </p>
      </div>
    </aside>
  );
}
